import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import buxusLogo from '../../assets/logo.png'
import buxusLogoLogin from '../../assets/buxus-logo-login.png'
import { isMobileWebViewport } from '../../platform/ui/mobileViewport'
import {
  trackButtonPressed,
  type ScreenInteractionContext,
} from '../../platform/analytics/interactionAnalytics'

const NAV_SIDEBAR_OPEN_STORAGE_KEY = 'buxus-web-nav-sidebar-open-v1'
const NAV_SIDEBAR_TRANSITION_MS = 300

function readOpenFromStorage(): boolean {
  try {
    const stored = sessionStorage.getItem(NAV_SIDEBAR_OPEN_STORAGE_KEY)
    if (stored !== null) return stored !== '0'
  } catch {
    /* private mode / blocked storage */
  }
  // Mobile browser: tuck sidebar away by default so map/content keeps width.
  return !isMobileWebViewport()
}

function writeOpenToStorage(open: boolean): void {
  try {
    sessionStorage.setItem(NAV_SIDEBAR_OPEN_STORAGE_KEY, open ? '1' : '0')
  } catch {
    /* ignore */
  }
}

type CollapsibleNavSidebarProps = {
  primaryNav: ReactNode
  operatorNav?: ReactNode
  analytics?: ScreenInteractionContext
}

export function CollapsibleNavSidebar({
  primaryNav,
  operatorNav,
  analytics,
}: CollapsibleNavSidebarProps) {
  const [open, setOpen] = useState(readOpenFromStorage)
  const openRef = useRef(open)
  openRef.current = open
  const analyticsRef = useRef(analytics)
  analyticsRef.current = analytics

  const toggleOpen = useCallback(() => {
    const next = !openRef.current
    const ctx = analyticsRef.current
    if (ctx) {
      trackButtonPressed({
        ...ctx,
        component: 'NavSidebar',
        button: next ? 'Expand navigation sidebar' : 'Collapse navigation sidebar',
      })
    }
    writeOpenToStorage(next)
    setOpen(next)
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, NAV_SIDEBAR_TRANSITION_MS + 20)
    return () => window.clearTimeout(timer)
  }, [open])

  return (
    <div className="nav-sidebar-shell" data-nav-expanded={open ? 'true' : 'false'}>
      <nav className="nav" aria-label="Primary">
        <div className="nav-sidebar-body">
          <div className="nav-sidebar-brand-row">
            <img
              className="nav-sidebar-logo"
              src={buxusLogo}
              alt="Buxus"
              width={1024}
              height={1024}
              decoding="async"
            />
          </div>

          {primaryNav}

          {operatorNav ? <div className="nav-sidebar-operator-block">{operatorNav}</div> : null}
        </div>

        <div className="nav-sidebar-footer-brand" aria-hidden={!open}>
          <img
            className="nav-sidebar-footer-logo"
            src={buxusLogoLogin}
            alt="Buxus"
            width={2550}
            height={740}
            decoding="async"
          />
        </div>
      </nav>

      <button
        type="button"
        className="nav-sidebar-drawer-handle"
        onClick={toggleOpen}
        aria-label={open ? 'Collapse navigation sidebar' : 'Expand navigation sidebar'}
        aria-expanded={open}
        title={open ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        <span className="nav-sidebar-drawer-handle-icon" aria-hidden>
          {open ? '‹' : '›'}
        </span>
      </button>
    </div>
  )
}
